import { Controller } from "@hotwired/stimulus";

/**
 * Trips Block Filter Controller
 *
 * Handles the filter bar functionality for the trips block,
 * including AJAX filtering and MultiSelect integration.
 */
export default class extends Controller {
  static targets = [
    "countries",
    "types",
    "categories",
    "sorting",
    "submitButton",
    "tripsList",
    "panel",
    "toggleButton",
    "filterIcon",
    "closeIcon",
    "form",
    "filterCount",
    "loadMoreSlide",
  ];

  static values = {
    nonce: String,
    ajaxUrl: String,
    currentPage: { type: Number, default: 1 },
    maxPages: { type: Number, default: 1 },
    isLoading: { type: Boolean, default: false },
  };

  connect() {
    this.panelOpen = false;

    // Update filter count after MultiSelect.js has initialized
    setTimeout(() => {
      this.updateFilterCount();
    }, 100);
  }

  /**
   * Toggle the filter panel visibility
   */
  togglePanel() {
    this.panelOpen = !this.panelOpen;

    if (this.hasPanelTarget) {
      this.panelTarget.classList.toggle("hidden", !this.panelOpen);
    }

    if (this.hasToggleButtonTarget) {
      this.toggleButtonTarget.setAttribute(
        "aria-expanded",
        this.panelOpen.toString()
      );
    }

    // Toggle icons visibility
    if (this.hasFilterIconTarget) {
      this.filterIconTarget.classList.toggle("hidden", this.panelOpen);
    }

    if (this.hasCloseIconTarget) {
      this.closeIconTarget.classList.toggle("hidden", !this.panelOpen);
    }
  }

  /**
   * Handle form submission - search for trips
   */
  search(event) {
    event.preventDefault();
    this.fetchTrips();
  }

  /**
   * Fetch trips via AJAX
   *
   * @param {number} page - Page number to fetch (default: 1)
   */
  fetchTrips(page = 1) {
    if (!this.hasSubmitButtonTarget) return;

    // Show loading state on submit button (only for initial search)
    let originalText = null;
    if (page === 1) {
      originalText = this.submitButtonTarget.innerHTML;
      this.submitButtonTarget.innerHTML = `
        <svg class="animate-spin -ml-1 mr-2 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Suche...
      `;
      this.submitButtonTarget.disabled = true;
    }

    // Build form data
    const data = new FormData();
    data.append("action", "trips_block_filter");
    data.append("nonce", this.nonceValue);
    data.append("countries", this.getSelectedValues("countries"));
    data.append("types", this.getSelectedValues("types"));
    data.append("categories", this.getSelectedValues("categories"));
    data.append("sorting", this.getSortingValue());
    data.append("paged", page);

    // Make AJAX request
    fetch(this.ajaxUrlValue, {
      method: "POST",
      credentials: "same-origin",
      body: data,
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.success && this.hasTripsListTarget) {
          // Update pagination values from response
          this.currentPageValue = response.data.current_page || 1;
          this.maxPagesValue = response.data.max_pages || 1;

          if (page === 1) {
            // Initial search: replace all content
            const swiperWrapper =
              this.tripsListTarget.querySelector(".swiper-wrapper");
            if (swiperWrapper) {
              // Wrap each card in swiper-slide
              const wrappedHtml = this.wrapInSwiperSlides(response.data.html);
              swiperWrapper.innerHTML = wrappedHtml;

              // Add "more" slide if there are more pages
              if (this.currentPageValue < this.maxPagesValue) {
                const remainingTrips =
                  response.data.found_posts -
                  this.currentPageValue * response.data.per_page;
                swiperWrapper.innerHTML += this.createLoadMoreSlideHtml(
                  remainingTrips
                );
              }

              // Reinitialize Swiper via the trips-slider controller
              this.reinitializeSwiper();
            } else {
              // Fallback if structure is different
              this.tripsListTarget.innerHTML = response.data.html;
            }
          } else {
            // Load more: append new trips
            this.appendTrips(response.data);
          }
        } else {
          console.error("Error fetching trips:", response);
        }
      })
      .catch((error) => {
        console.error("Error fetching trips:", error);
      })
      .finally(() => {
        // Reset button state (only for initial search)
        if (page === 1 && originalText !== null) {
          this.submitButtonTarget.innerHTML = originalText;
          this.submitButtonTarget.disabled = false;
        }

        // Reset loading state
        this.isLoadingValue = false;

        // Update the filter count badge
        this.updateFilterCount();
      });
  }

  /**
   * Get selected values for a multiselect field
   * MultiSelect.js creates hidden inputs with name="fieldname[]"
   *
   * @param {string} fieldName - The field name (countries, types, categories)
   * @returns {string} Comma-separated list of selected values
   */
  getSelectedValues(fieldName) {
    // MultiSelect.js creates hidden inputs with the select's name
    const hiddenInputs = this.element.querySelectorAll(
      `input[type="hidden"][name="${fieldName}[]"]`
    );

    const values = [];
    hiddenInputs.forEach((input) => {
      if (input.value) {
        values.push(parseInt(input.value, 10));
      }
    });

    return values.join(",");
  }

  /**
   * Get the sorting value
   *
   * @returns {string} The selected sorting value
   */
  getSortingValue() {
    if (this.hasSortingTarget) {
      return this.sortingTarget.value || "";
    }
    return "";
  }

  /**
   * Count and display active filters
   * Counts countries, types, and categories (not sorting)
   */
  updateFilterCount() {
    if (!this.hasFilterCountTarget) return;

    let count = 0;

    // Count selected countries
    const countriesCount = this.element.querySelectorAll(
      'input[type="hidden"][name="countries[]"]'
    ).length;
    if (countriesCount > 0) count++;

    // Count selected types
    const typesCount = this.element.querySelectorAll(
      'input[type="hidden"][name="types[]"]'
    ).length;
    if (typesCount > 0) count++;

    // Count selected categories
    const categoriesCount = this.element.querySelectorAll(
      'input[type="hidden"][name="categories[]"]'
    ).length;
    if (categoriesCount > 0) count++;

    // Update the badge
    if (count > 0) {
      this.filterCountTarget.textContent = count;
      this.filterCountTarget.classList.remove("hidden");
      this.filterCountTarget.classList.add("flex");
    } else {
      this.filterCountTarget.classList.add("hidden");
      this.filterCountTarget.classList.remove("flex");
    }
  }

  /**
   * Wrap trip cards HTML in swiper-slide divs
   *
   * @param {string} html - Raw HTML containing trip cards
   * @returns {string} HTML with each card wrapped in swiper-slide
   */
  wrapInSwiperSlides(html) {
    // Create a temporary container to parse the HTML
    const temp = document.createElement("div");
    temp.innerHTML = html;

    // Check if we got a "no results" message (single div with text)
    const children = [...temp.children];
    if (children.length === 1 && children[0].classList.contains("text-center")) {
      // No results message - wrap as full-width slide
      return `<div class="swiper-slide w-full!">${html}</div>`;
    }

    // Wrap each visible element in a swiper-slide, skipping non-visual
    // elements like <style>/<script> which would create zero-width slides
    // and corrupt Swiper's snap grid calculations (crashes iOS Safari).
    let wrappedHtml = "";
    for (const child of children) {
      const tag = child.tagName.toLowerCase();
      if (tag === "style" || tag === "script" || tag === "link") {
        continue;
      }
      wrappedHtml += `<div class="swiper-slide w-auto!">${child.outerHTML}</div>`;
    }

    return wrappedHtml;
  }

  /**
   * Reinitialize the Swiper slider after AJAX content update
   */
  reinitializeSwiper() {
    if (!this.hasTripsListTarget) return;

    // Get the trips-slider controller from the tripsList element
    const swiperController = this.application.getControllerForElementAndIdentifier(
      this.tripsListTarget,
      "trips-slider"
    );

    if (swiperController && typeof swiperController.reinitialize === "function") {
      swiperController.reinitialize();
    }
  }

  /**
   * Handle click on "load more" slide
   *
   * @param {Event} event - Click event
   */
  loadMore(event) {
    event.preventDefault();

    // Prevent double-clicks
    if (this.isLoadingValue) return;

    this.isLoadingValue = true;
    this.updateLoadMoreSlideLoading(true);

    // Fetch next page
    const nextPage = this.currentPageValue + 1;
    this.fetchTrips(nextPage);
  }

  /**
   * Create HTML for the "load more" slide
   *
   * @param {number} remainingTrips - Number of remaining trips to load
   * @returns {string} HTML string for the slide
   */
  createLoadMoreSlideHtml(remainingTrips) {
    return `
      <div
        class="swiper-slide w-auto!"
        data-trips-block-filter-target="loadMoreSlide"
      >
        <button
          type="button"
          class="load-more-slide flex flex-col items-center justify-center w-[345px] min-h-[400px] bg-nr-dark-cream rounded-lg cursor-pointer border-0 transition-all hover:bg-nr-dark-cream/80 group"
          data-action="trips-block-filter#loadMore"
        >
          <span class="load-more-content flex flex-col items-center gap-4">
            <span class="w-16 h-16 rounded-full bg-nr-green flex items-center justify-center transition-transform group-hover:scale-110">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
            </span>
            <span class="font-sans font-semibold text-lg text-nr-text">Mehr Reisen laden</span>
            <span class="font-sans text-sm text-nr-text/70">${remainingTrips} weitere Reisen</span>
          </span>
          <span class="load-more-loading hidden flex-col items-center gap-4">
            <svg class="animate-spin w-12 h-12 text-nr-green" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span class="font-sans text-sm text-nr-text/70">Lade mehr Reisen...</span>
          </span>
        </button>
      </div>
    `;
  }

  /**
   * Update the "load more" slide to show loading state
   *
   * @param {boolean} isLoading - Whether to show loading state
   */
  updateLoadMoreSlideLoading(isLoading) {
    if (!this.hasLoadMoreSlideTarget) return;

    const content = this.loadMoreSlideTarget.querySelector(".load-more-content");
    const loading = this.loadMoreSlideTarget.querySelector(".load-more-loading");

    if (content && loading) {
      content.classList.toggle("hidden", isLoading);
      content.classList.toggle("flex", !isLoading);
      loading.classList.toggle("hidden", !isLoading);
      loading.classList.toggle("flex", isLoading);
    }
  }

  /**
   * Remove the "load more" slide
   */
  removeLoadMoreSlide() {
    if (!this.hasLoadMoreSlideTarget || !this.hasTripsListTarget) return;

    const swiperController = this.application.getControllerForElementAndIdentifier(
      this.tripsListTarget,
      "trips-slider"
    );

    if (swiperController && swiperController.swiper) {
      const slides = swiperController.swiper.slides;
      const loadMoreIndex = Array.from(slides).findIndex(
        (slide) => slide === this.loadMoreSlideTarget
      );

      if (loadMoreIndex !== -1) {
        swiperController.removeSlideByIndex(loadMoreIndex);
      }
    }
  }

  /**
   * Append new trips to the slider
   *
   * @param {Object} data - Response data from AJAX
   */
  appendTrips(data) {
    if (!this.hasTripsListTarget) return;

    const swiperWrapper = this.tripsListTarget.querySelector(".swiper-wrapper");
    if (!swiperWrapper) return;

    // Using direct DOM updates plus full reinitialize avoids iOS crashes
    // when removing/appending the active last slide via Swiper manipulation APIs.
    swiperWrapper.querySelectorAll(".load-more-slide").forEach((button) => {
      const slide = button.closest(".swiper-slide");
      if (slide) {
        slide.remove();
      }
    });

    // Append newly fetched trip cards.
    swiperWrapper.insertAdjacentHTML("beforeend", this.wrapInSwiperSlides(data.html));

    // Re-add the load more slide if there are still pages left.
    if (this.currentPageValue < this.maxPagesValue) {
      const remainingTrips = data.found_posts - this.currentPageValue * data.per_page;
      swiperWrapper.insertAdjacentHTML(
        "beforeend",
        this.createLoadMoreSlideHtml(remainingTrips)
      );
    }

    this.updateSwiperSlides();
  }

  /**
   * Lightweight Swiper update after appending slides.
   * Uses swiper.update() instead of full destroy+recreate
   * to avoid iOS Safari crashes from layout reflows.
   */
  updateSwiperSlides() {
    if (!this.hasTripsListTarget) return;

    const swiperController =
      this.application.getControllerForElementAndIdentifier(
        this.tripsListTarget,
        "trips-slider"
      );

    if (swiperController && typeof swiperController.updateSlides === "function") {
      swiperController.updateSlides();
    }
  }
}
